/* SalesProspects — searchable / filterable table of all prospects.
 *
 * iter98.52 — Clicking a row opens a side-panel modal with the full
 * doctor activation detail (profile · stage · timeline · activity log ·
 * quick-action buttons).
 *
 * Rows are colour-coded by health_status (green / yellow / red).
 * Search box reads ?q= from the URL so the top-nav search box can
 * deep-link here with a pre-filled query.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    MagnifyingGlass, Funnel, X, Phone, EnvelopeSimple, Calendar, Stack,
    ChartLine, FileText, ArrowRight, CheckCircle, Warning, ArrowUpRight,
    Buildings, MapPin, Tag, ChatCircleText, ClockCounterClockwise, PencilSimple,
    CheckSquare, Circle, FilePdf, Trash, Plus,
} from "@phosphor-icons/react";


const STAGE_META = {
    prospect:    { label: "Prospect",    color: "#94A3B8" },
    discovery:   { label: "Discovery",   color: "#0EA5E9" },
    demo:        { label: "Lab Demo",    color: "#6366F1" },
    trial:       { label: "Case Trial",  color: "#F59E0B" },
    negotiation: { label: "Negotiation", color: "#EC4899" },
    contract:    { label: "Contract",    color: "#10B981" },
    active:      { label: "Active",      color: "#059669" },
};
const STAGE_ORDER = ["prospect", "discovery", "demo", "trial", "negotiation", "contract", "active"];

const TIER_LABEL = {
    startup: "Startup", growth: "Growth", established: "Established", multi_clinic: "Multi-clinic",
};
const TIER_TINT = {
    startup: "bg-slate-50 text-slate-700 ring-slate-200",
    growth: "bg-sky-50 text-sky-700 ring-sky-200",
    established: "bg-primary/10 text-primary ring-primary/20",
    multi_clinic: "bg-amber-50 text-amber-700 ring-amber-200",
};

const SCANNER_LABEL = {
    none: "—", itero_5d: "iTero 5D", trios: "TRIOS", carestream: "Carestream", medit: "Medit", other: "Other",
};


function fmtINR(n) {
    if (!n) return "—";
    if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
    if (n >= 1000)   return `₹${(n / 1000).toFixed(0)}k`;
    return `₹${n}`;
}

function fmtDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return "—"; }
}


export default function SalesProspects() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [prospects, setProspects] = useState([]);
    const [reps, setReps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [isManager, setIsManager] = useState(false);

    const initialQ = searchParams.get("q") || "";
    const [q, setQ] = useState(initialQ);
    const [filters, setFilters] = useState({ stage: "", tier: "", rep: "", territory: "" });

    useEffect(() => {
        let alive = true;
        Promise.all([
            api.get("/sales/prospects"),
            api.get("/sales/reps"),
            api.get("/sales/dashboard"),
        ])
            .then(([p, r, d]) => {
                if (!alive) return;
                setProspects(p.data || []);
                setReps(r.data || []);
                setIsManager(d.data?.is_manager || false);
            })
            .catch(() => null)
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    // Open the detail modal automatically when /sales/prospects/:id is hit.
    useEffect(() => {
        const segs = window.location.pathname.split("/").filter(Boolean);
        const last = segs[segs.length - 1];
        if (last && last !== "prospects") {
            setSelected(last);
        }
    }, []);

    const filtered = useMemo(() => {
        const qLower = q.trim().toLowerCase();
        return prospects.filter((p) => {
            if (filters.stage && p.pipeline_stage !== filters.stage) return false;
            if (filters.tier && p.clinic_tier !== filters.tier) return false;
            if (filters.rep && p.assigned_rep_id !== filters.rep) return false;
            if (filters.territory && p.territory !== filters.territory) return false;
            if (qLower) {
                const hay = `${p.clinic_name} ${p.doctor_name} ${p.city}`.toLowerCase();
                if (!hay.includes(qLower)) return false;
            }
            return true;
        });
    }, [prospects, q, filters]);

    const onUpdated = (updated) => {
        setProspects((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
    };

    return (
        <div className="p-4 sm:p-6 space-y-4 max-w-[1400px] mx-auto" data-testid="sales-prospects">
            <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="font-display text-2xl font-bold tracking-tight">Prospects</h1>
                    <p className="text-xs text-muted-foreground mt-1">
                        {filtered.length} of {prospects.length} doctors visible · click a row for full detail.
                    </p>
                </div>
            </div>

            {/* Search + filter bar */}
            <div className="bg-white rounded-2xl border border-border p-3 flex items-center gap-2 flex-wrap" data-testid="prospects-filter-bar">
                <div className="flex items-center gap-2 bg-secondary/60 rounded-md px-2.5 h-8 flex-1 min-w-[200px]">
                    <MagnifyingGlass size={13} className="text-muted-foreground" />
                    <input
                        type="text"
                        value={q}
                        onChange={(e) => {
                            setQ(e.target.value);
                            const sp = new URLSearchParams(searchParams);
                            if (e.target.value) sp.set("q", e.target.value); else sp.delete("q");
                            setSearchParams(sp, { replace: true });
                        }}
                        placeholder="Search clinic / doctor / city"
                        className="bg-transparent flex-1 text-xs outline-none placeholder:text-muted-foreground"
                        data-testid="prospects-search-input"
                    />
                </div>
                <FilterSelect value={filters.stage} onChange={(v) => setFilters({ ...filters, stage: v })} placeholder="All stages" options={STAGE_ORDER.map((s) => ({ value: s, label: STAGE_META[s].label }))} testid="prospects-filter-stage" />
                <FilterSelect value={filters.tier} onChange={(v) => setFilters({ ...filters, tier: v })} placeholder="All tiers" options={Object.entries(TIER_LABEL).map(([k, v]) => ({ value: k, label: v }))} testid="prospects-filter-tier" />
                {isManager && (
                    <FilterSelect value={filters.rep} onChange={(v) => setFilters({ ...filters, rep: v })} placeholder="All reps" options={reps.filter((r) => r.role !== "admin").map((r) => ({ value: r.id, label: r.name || "—" }))} testid="prospects-filter-rep" />
                )}
                {(filters.stage || filters.tier || filters.rep || q) && (
                    <button
                        type="button"
                        onClick={() => { setFilters({ stage: "", tier: "", rep: "", territory: "" }); setQ(""); setSearchParams({}, { replace: true }); }}
                        className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 px-2 h-7"
                        data-testid="prospects-clear-filters"
                    >
                        <X size={11} /> Clear
                    </button>
                )}
            </div>

            {/* Table */}
            <div className="bg-white rounded-2xl border border-border overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="prospects-table">
                        <thead>
                            <tr className="bg-secondary/30 border-b border-border">
                                <th className="text-left px-4 py-2.5 font-semibold uppercase text-[10px] tracking-wider text-muted-foreground">Clinic</th>
                                <th className="text-left px-4 py-2.5 font-semibold uppercase text-[10px] tracking-wider text-muted-foreground">Doctor · Location</th>
                                <th className="text-left px-4 py-2.5 font-semibold uppercase text-[10px] tracking-wider text-muted-foreground">Tier</th>
                                <th className="text-left px-4 py-2.5 font-semibold uppercase text-[10px] tracking-wider text-muted-foreground">Scanner</th>
                                <th className="text-left px-4 py-2.5 font-semibold uppercase text-[10px] tracking-wider text-muted-foreground">Stage</th>
                                <th className="text-right px-4 py-2.5 font-semibold uppercase text-[10px] tracking-wider text-muted-foreground">Cases/mo</th>
                                <th className="text-right px-4 py-2.5 font-semibold uppercase text-[10px] tracking-wider text-muted-foreground">Value/mo</th>
                                <th className="text-left px-4 py-2.5 font-semibold uppercase text-[10px] tracking-wider text-muted-foreground">Rep</th>
                                <th className="px-2 py-2.5"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>
                            )}
                            {!loading && filtered.length === 0 && (
                                <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">No prospects match — adjust filters.</td></tr>
                            )}
                            {filtered.map((p) => {
                                const stageMeta = STAGE_META[p.pipeline_stage] || STAGE_META.prospect;
                                const tierTint = TIER_TINT[p.clinic_tier] || TIER_TINT.growth;
                                const rowAccent = p.health_status === "red"   ? "border-l-rose-400" :
                                                  p.health_status === "yellow" ? "border-l-amber-400" :
                                                                                 "border-l-emerald-400";
                                return (
                                    <tr
                                        key={p.id}
                                        onClick={() => setSelected(p.id)}
                                        className={`border-b border-border/60 hover:bg-secondary/30 cursor-pointer border-l-2 ${rowAccent}`}
                                        data-testid={`prospect-row-${p.id}`}
                                    >
                                        <td className="px-4 py-2.5">
                                            <div className="font-semibold">{p.clinic_name}</div>
                                            {p.branch_count > 1 && (
                                                <div className="text-[10px] text-muted-foreground">{p.branch_count} branches</div>
                                            )}
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <div className="text-foreground">{p.doctor_name}</div>
                                            <div className="text-[10px] text-muted-foreground">
                                                {p.city}{p.territory ? ` · ${p.territory.toUpperCase()}` : ""}
                                            </div>
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ring-1 ${tierTint}`}>
                                                {TIER_LABEL[p.clinic_tier]}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2.5 text-muted-foreground">{SCANNER_LABEL[p.scanner_type] || "—"}</td>
                                        <td className="px-4 py-2.5">
                                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-secondary/60 text-[10px] font-semibold">
                                                <span className="w-1.5 h-1.5 rounded-full" style={{ background: stageMeta.color }} />
                                                {stageMeta.label}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2.5 text-right tabular-nums font-semibold">
                                            {p.pipeline_stage === "active" ? p.cases_submitted_this_month : p.estimated_monthly_cases}
                                        </td>
                                        <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-primary">
                                            {fmtINR(p.pipeline_stage === "active" ? p.monthly_revenue_this_month : p.estimated_monthly_revenue)}
                                        </td>
                                        <td className="px-4 py-2.5 text-muted-foreground">{p.rep_name || "—"}</td>
                                        <td className="px-2 py-2.5">
                                            <ArrowRight size={12} className="text-muted-foreground" />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Detail side-panel modal */}
            {selected && (
                <ProspectDetailPanel
                    prospectId={selected}
                    onClose={() => { setSelected(null); navigate("/sales/prospects", { replace: true }); }}
                    onUpdated={onUpdated}
                    isManager={isManager}
                    reps={reps}
                />
            )}
        </div>
    );
}


/* ──────────── Detail side-panel ──────────── */
function ProspectDetailPanel({ prospectId, onClose, onUpdated, isManager, reps }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        api.get(`/sales/prospects/${prospectId}`)
            .then((r) => { if (alive) setData(r.data); })
            .catch(() => null)
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [prospectId]);

    const refetch = async () => {
        try {
            const r = await api.get(`/sales/prospects/${prospectId}`);
            setData(r.data);
            onUpdated && onUpdated(r.data);
        } catch { /* swallow */ }
    };

    const stageMeta = data ? (STAGE_META[data.pipeline_stage] || STAGE_META.prospect) : null;

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
                onClick={onClose}
                data-testid="prospect-detail-backdrop"
            />
            {/* Panel — slides in from the right */}
            <div
                className="fixed top-0 right-0 bottom-0 w-full max-w-[600px] bg-white border-l border-border z-50 overflow-y-auto shadow-2xl"
                data-testid="prospect-detail-panel"
            >
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-border z-10 p-5 flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                        {loading ? (
                            <div className="text-sm text-muted-foreground">Loading…</div>
                        ) : data ? (
                            <>
                                <h2 className="font-display text-xl font-bold tracking-tight truncate">{data.clinic_name}</h2>
                                <div className="text-xs text-muted-foreground mt-0.5 truncate">
                                    {data.doctor_name} · {data.city}{data.state ? `, ${data.state}` : ""}
                                </div>
                                <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                                    {stageMeta && (
                                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-secondary text-[10px] font-bold">
                                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: stageMeta.color }} />
                                            {stageMeta.label}
                                        </span>
                                    )}
                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ring-1 ${TIER_TINT[data.clinic_tier]}`}>
                                        {TIER_LABEL[data.clinic_tier]}
                                    </span>
                                    {data.rep_name && (
                                        <span className="text-[10px] text-muted-foreground">Rep: {data.rep_name}</span>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="text-sm text-rose-600">Couldn&apos;t load this prospect.</div>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-8 h-8 rounded-full hover:bg-secondary grid place-items-center shrink-0"
                        data-testid="prospect-detail-close"
                    >
                        <X size={14} />
                    </button>
                </div>

                {data && (
                    <div className="p-5 space-y-5">
                        {/* Quick actions */}
                        <QuickActions data={data} reps={reps} isManager={isManager} onRefetch={refetch} />

                        {/* Stage-specific metrics */}
                        <StageMetrics data={data} />

                        {/* Doctor profile facts */}
                        <FactGrid data={data} />

                        {/* iter98.53 — Per-prospect tasks panel */}
                        <TaskPanel prospectId={data.id} />

                        {/* Activity log */}
                        <ActivityLog activities={data.activities || []} prospectId={data.id} onLogged={refetch} />
                    </div>
                )}
            </div>
        </>
    );
}


/* Quick action row — Log call · Send email · Move stage · Reassign · Edit */
function QuickActions({ data, reps, isManager, onRefetch }) {
    const [showLog, setShowLog] = useState(null);     // "call" | "email" | "note" | "meeting"
    const [showStageChange, setShowStageChange] = useState(false);

    const moveStage = async (newStage) => {
        try {
            await api.patch(`/sales/prospects/${data.id}`, { pipeline_stage: newStage });
            toast.success(`Moved to ${STAGE_META[newStage].label}`);
            setShowStageChange(false);
            onRefetch();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't update stage.");
        }
    };

    return (
        <div className="bg-secondary/30 rounded-xl p-3 space-y-2" data-testid="prospect-quick-actions">
            <div className="flex items-center gap-2 flex-wrap">
                <ActionBtn icon={Phone}            label="Log call"      onClick={() => setShowLog("call")}    testid="qa-log-call" />
                <ActionBtn icon={EnvelopeSimple}   label="Log email"     onClick={() => setShowLog("email")}   testid="qa-log-email" />
                <ActionBtn icon={Calendar}         label="Log meeting"   onClick={() => setShowLog("meeting")} testid="qa-log-meeting" />
                <ActionBtn icon={FileText}         label="Add note"      onClick={() => setShowLog("note")}    testid="qa-log-note" />
                <ActionBtn icon={ArrowUpRight}     label="Move stage"    onClick={() => setShowStageChange(!showStageChange)} testid="qa-move-stage" />
                <ProposalButton prospectId={data.id} clinicName={data.clinic_name} />
            </div>

            {/* Inline stage picker */}
            {showStageChange && (
                <div className="bg-white rounded-lg border border-border p-2 flex gap-1.5 flex-wrap" data-testid="stage-picker">
                    {STAGE_ORDER.map((s) => (
                        <button
                            key={s}
                            type="button"
                            onClick={() => moveStage(s)}
                            className={`text-[10px] font-semibold px-2 py-1 rounded-md ${
                                s === data.pipeline_stage
                                    ? "bg-primary text-white"
                                    : "bg-secondary hover:bg-primary/10"
                            }`}
                            data-testid={`stage-picker-${s}`}
                        >
                            {STAGE_META[s].label}
                        </button>
                    ))}
                </div>
            )}

            {/* Inline log composer */}
            {showLog && (
                <ActivityComposer
                    type={showLog}
                    prospectId={data.id}
                    onClose={() => setShowLog(null)}
                    onLogged={() => { setShowLog(null); onRefetch(); }}
                />
            )}
        </div>
    );
}

function ActionBtn({ icon: Icon, label, onClick, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-white border border-border rounded-full px-2.5 py-1 hover:bg-primary/5 hover:border-primary/40 transition-colors"
            data-testid={testid}
        >
            <Icon size={11} weight="duotone" /> {label}
        </button>
    );
}


function ActivityComposer({ type, prospectId, onClose, onLogged }) {
    const [summary, setSummary] = useState("");
    const [busy, setBusy] = useState(false);
    const placeholder = {
        call:    "30-min call · discussed onboarding timeline · follow up next week",
        email:   "Sent pricing deck · awaiting reply",
        meeting: "On-site meeting · met Dr. & practice manager · next: technical demo",
        note:    "Internal note",
    }[type] || "";

    const save = async () => {
        if (!summary.trim()) { toast.error("Add a quick summary"); return; }
        setBusy(true);
        try {
            await api.post(`/sales/prospects/${prospectId}/activities`, { type, summary: summary.trim() });
            toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} logged`);
            onLogged();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't log activity.");
        } finally { setBusy(false); }
    };

    return (
        <div className="bg-white rounded-lg border border-primary/40 p-3 space-y-2" data-testid={`activity-composer-${type}`}>
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Log {type}</div>
            <textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder={placeholder}
                className="w-full border border-border rounded-md p-2 text-xs outline-none focus:border-primary min-h-[80px]"
                data-testid="composer-textarea"
            />
            <div className="flex items-center justify-end gap-1.5">
                <button type="button" onClick={onClose} className="text-[11px] text-muted-foreground hover:text-foreground px-2 py-1" data-testid="composer-cancel">Cancel</button>
                <button
                    type="button"
                    disabled={busy || !summary.trim()}
                    onClick={save}
                    className="text-[11px] font-semibold bg-primary text-white rounded-full px-3 py-1 disabled:opacity-50"
                    data-testid="composer-save"
                >
                    {busy ? "Saving…" : "Save"}
                </button>
            </div>
        </div>
    );
}


/* Stage-specific metric block */
function StageMetrics({ data }) {
    const s = data.pipeline_stage;
    if (s === "trial") {
        return (
            <section data-testid="stage-metrics-trial">
                <SectionTitle>Trial metrics</SectionTitle>
                <div className="grid grid-cols-2 gap-2">
                    <MetricBox label="Pilot cases submitted" value={data.pilot_cases_submitted || 0} />
                    <MetricBox label="Pilot cases approved"  value={data.pilot_cases_approved || 0} />
                    <MetricBox label="Avg quality score"     value={data.pilot_quality_score ? `${data.pilot_quality_score} / 5` : "—"} />
                    <MetricBox label="Avg turnaround"        value={data.pilot_turnaround_days ? `${data.pilot_turnaround_days} d` : "—"} />
                </div>
            </section>
        );
    }
    if (s === "contract" || s === "active") {
        return (
            <section data-testid={`stage-metrics-${s}`}>
                <SectionTitle>{s === "active" ? "Live performance" : "Contract details"}</SectionTitle>
                <div className="grid grid-cols-2 gap-2">
                    <MetricBox label="Contract tier"    value={data.contract_tier ? TIER_LABEL[data.contract_tier] : "—"} />
                    <MetricBox label="Per-case price"   value={data.contract_price_per_case ? `₹${data.contract_price_per_case.toLocaleString("en-IN")}` : "—"} />
                    {s === "active" && (
                        <>
                            <MetricBox label="Cases this month"   value={data.cases_submitted_this_month || 0} />
                            <MetricBox label="Cases last month"   value={data.cases_submitted_last_month || 0} />
                            <MetricBox label="Monthly revenue"    value={fmtINR(data.monthly_revenue_this_month)} />
                            <MetricBox label="Activated on"       value={fmtDate(data.activation_date)} />
                        </>
                    )}
                </div>
            </section>
        );
    }
    return null;
}

function MetricBox({ label, value }) {
    return (
        <div className="bg-secondary/30 rounded-lg px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="text-sm font-bold tabular-nums mt-0.5">{value}</div>
        </div>
    );
}


/* Doctor profile facts — always shown */
function FactGrid({ data }) {
    return (
        <section data-testid="prospect-fact-grid">
            <SectionTitle>Profile</SectionTitle>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                <Fact label="Phone"             value={data.doctor_phone || "—"} icon={Phone} />
                <Fact label="Email"             value={data.doctor_email || "—"} icon={EnvelopeSimple} />
                <Fact label="Branches"          value={data.branch_count || 1} icon={Buildings} />
                <Fact label="Scanner"           value={SCANNER_LABEL[data.scanner_type] || "—"} icon={ChartLine} />
                <Fact label="Current provider"  value={data.current_aligner_provider && data.current_aligner_provider !== "none" ? data.current_aligner_provider : "None"} icon={Tag} />
                <Fact label="Est. cases/mo"     value={data.estimated_monthly_cases || 0} icon={Stack} />
                <Fact label="Est. value/mo"     value={fmtINR(data.estimated_monthly_revenue)} icon={Tag} />
                <Fact label="Last contact"      value={fmtDate(data.last_contact_date)} icon={Calendar} />
            </div>
        </section>
    );
}

function Fact({ label, value, icon: Icon }) {
    return (
        <div className="flex items-start gap-2 py-0.5">
            <Icon size={11} weight="duotone" className="text-muted-foreground shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
                <div className="text-xs font-medium truncate">{value}</div>
            </div>
        </div>
    );
}


/* Activity log — reverse chronological */
const ACTIVITY_ICON = {
    call: Phone, email: EnvelopeSimple, meeting: Calendar, demo: ChartLine,
    note: FileText, stage_change: ArrowUpRight, case_submitted: Stack,
    proposal_sent: FileText, contract_signed: CheckCircle,
};
const ACTIVITY_TINT = {
    call: "bg-sky-50 text-sky-700", email: "bg-indigo-50 text-indigo-700",
    meeting: "bg-violet-50 text-violet-700", demo: "bg-amber-50 text-amber-700",
    note: "bg-slate-50 text-slate-700", stage_change: "bg-primary/10 text-primary",
    case_submitted: "bg-emerald-50 text-emerald-700",
    proposal_sent: "bg-rose-50 text-rose-700", contract_signed: "bg-emerald-50 text-emerald-700",
};

function ActivityLog({ activities }) {
    return (
        <section data-testid="prospect-activity-log">
            <SectionTitle>Activity log</SectionTitle>
            {activities.length === 0 ? (
                <div className="text-[11px] text-muted-foreground italic py-3">No activity logged yet — use the buttons above to log a call, email or note.</div>
            ) : (
                <div className="space-y-2">
                    {activities.map((a) => {
                        const Icon = ACTIVITY_ICON[a.type] || FileText;
                        const tint = ACTIVITY_TINT[a.type] || "bg-slate-50 text-slate-700";
                        return (
                            <div key={a.id} className="flex items-start gap-2.5" data-testid={`activity-${a.id}`}>
                                <div className={`w-7 h-7 rounded-md grid place-items-center shrink-0 ${tint}`}>
                                    <Icon size={13} weight="duotone" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs">
                                        <span className="font-semibold capitalize">{a.type.replace(/_/g, " ")}</span>
                                        <span className="text-muted-foreground"> · {a.summary}</span>
                                    </div>
                                    <div className="text-[10px] text-muted-foreground">
                                        {a.rep_name && <>{a.rep_name} · </>}{fmtDate(a.when)}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </section>
    );
}


function SectionTitle({ children }) {
    return <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-2">{children}</div>;
}

function FilterSelect({ value, onChange, placeholder, options, testid }) {
    return (
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="h-8 px-2 text-xs rounded-md border border-border bg-white outline-none focus:ring-2 focus:ring-primary/30"
            data-testid={testid}
        >
            <option value="">{placeholder}</option>
            {options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
            ))}
        </select>
    );
}



/* ──────────── Task panel (per-prospect) ──────────── */
function TaskPanel({ prospectId }) {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [title, setTitle] = useState("");
    const [dueDate, setDueDate] = useState("");
    const [busy, setBusy] = useState(false);

    const fetchTasks = async () => {
        try {
            const r = await api.get(`/sales/tasks`, { params: { prospect: prospectId } });
            setTasks(r.data?.tasks || []);
        } catch { /* swallow */ }
        finally { setLoading(false); }
    };

    useEffect(() => {
        let alive = true;
        api.get(`/sales/tasks`, { params: { prospect: prospectId } })
            .then((r) => { if (alive) setTasks(r.data?.tasks || []); })
            .catch(() => null)
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [prospectId]);

    const addTask = async () => {
        if (!title.trim()) { toast.error("Title required"); return; }
        setBusy(true);
        try {
            await api.post(`/sales/prospects/${prospectId}/tasks`, {
                title: title.trim(),
                due_date: dueDate || null,
            });
            toast.success("Task added");
            setTitle("");
            setDueDate("");
            setShowAdd(false);
            await fetchTasks();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't add task.");
        } finally { setBusy(false); }
    };

    const toggleTask = async (taskId, currentStatus) => {
        const next = currentStatus === "done" ? "open" : "done";
        try {
            await api.patch(`/sales/tasks/${taskId}`, { status: next });
            await fetchTasks();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't update task.");
        }
    };

    const deleteTask = async (taskId) => {
        try {
            await api.delete(`/sales/tasks/${taskId}`);
            await fetchTasks();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't delete task.");
        }
    };

    const today = new Date().toISOString().slice(0, 10);
    const open = tasks.filter((t) => t.status === "open");
    const done = tasks.filter((t) => t.status !== "open");

    return (
        <section data-testid="prospect-task-panel">
            <div className="flex items-center justify-between mb-2">
                <SectionTitle>Tasks ({open.length} open)</SectionTitle>
                <button
                    type="button"
                    onClick={() => setShowAdd(!showAdd)}
                    className="text-[10px] font-bold uppercase tracking-wider text-primary inline-flex items-center gap-1 hover:underline"
                    data-testid="task-panel-add-btn"
                >
                    <Plus size={11} weight="bold" /> Add task
                </button>
            </div>

            {showAdd && (
                <div className="bg-white rounded-lg border border-primary/40 p-3 space-y-2 mb-2" data-testid="task-composer">
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="e.g. Send pricing proposal · book on-site demo · follow up on contract"
                        className="w-full border border-border rounded-md p-2 text-xs outline-none focus:border-primary"
                        data-testid="task-composer-title"
                        autoFocus
                    />
                    <div className="flex items-center gap-2">
                        <input
                            type="date"
                            value={dueDate}
                            min={today}
                            onChange={(e) => setDueDate(e.target.value)}
                            className="border border-border rounded-md p-1.5 text-xs outline-none focus:border-primary"
                            data-testid="task-composer-due-date"
                        />
                        <button
                            type="button"
                            onClick={() => setShowAdd(false)}
                            className="text-[11px] text-muted-foreground hover:text-foreground px-2 py-1"
                            data-testid="task-composer-cancel"
                        >Cancel</button>
                        <button
                            type="button"
                            onClick={addTask}
                            disabled={busy || !title.trim()}
                            className="text-[11px] font-semibold bg-primary text-white rounded-full px-3 py-1 disabled:opacity-50"
                            data-testid="task-composer-save"
                        >
                            {busy ? "Saving…" : "Add task"}
                        </button>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="text-[11px] text-muted-foreground italic py-3">Loading tasks…</div>
            ) : tasks.length === 0 ? (
                <div className="text-[11px] text-muted-foreground italic py-3">No tasks yet. Add one to track a follow-up or deadline.</div>
            ) : (
                <div className="space-y-1.5">
                    {open.map((t) => <TaskPanelRow key={t.id} task={t} onToggle={() => toggleTask(t.id, t.status)} onDelete={() => deleteTask(t.id)} />)}
                    {done.length > 0 && (
                        <>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-3 mb-1">Completed</div>
                            {done.map((t) => <TaskPanelRow key={t.id} task={t} onToggle={() => toggleTask(t.id, t.status)} onDelete={() => deleteTask(t.id)} done />)}
                        </>
                    )}
                </div>
            )}
        </section>
    );
}

function TaskPanelRow({ task, onToggle, onDelete, done }) {
    const today = new Date().toISOString().slice(0, 10);
    const overdue = !done && task.due_date && task.due_date < today;
    return (
        <div
            className={`flex items-start gap-2 p-2 rounded-md border ${overdue ? "border-rose-200 bg-rose-50/40" : "border-border bg-white"}`}
            data-testid={`task-panel-row-${task.id}`}
        >
            <button
                type="button"
                onClick={onToggle}
                className={`shrink-0 mt-0.5 ${done ? "text-emerald-600" : overdue ? "text-rose-600 hover:text-emerald-600" : "text-muted-foreground hover:text-emerald-600"}`}
                data-testid={`task-panel-${task.id}-toggle`}
                aria-label={done ? "Reopen task" : "Mark task done"}
            >
                {done ? <CheckCircle size={15} weight="fill" /> : <Circle size={15} />}
            </button>
            <div className="flex-1 min-w-0">
                <div className={`text-xs font-medium ${done ? "line-through text-muted-foreground" : ""}`}>{task.title}</div>
                {task.due_date && (
                    <div className={`text-[10px] mt-0.5 ${overdue ? "text-rose-600 font-bold" : "text-muted-foreground"}`}>
                        Due {fmtDate(task.due_date)}{overdue && " · overdue"}
                    </div>
                )}
            </div>
            <button
                type="button"
                onClick={onDelete}
                className="text-muted-foreground hover:text-rose-600 shrink-0"
                data-testid={`task-panel-${task.id}-delete`}
                aria-label="Delete task"
            >
                <Trash size={11} />
            </button>
        </div>
    );
}


/* ──────────── Generate Proposal button ──────────── */
function ProposalButton({ prospectId, clinicName }) {
    const [busy, setBusy] = useState(false);

    const generate = async () => {
        setBusy(true);
        try {
            const r = await api.get(`/sales/prospects/${prospectId}/proposal.pdf`, { responseType: "blob" });
            const blob = new Blob([r.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            // Open in a new tab — the user can save / print from there.
            const w = window.open(url, "_blank");
            if (!w) {
                // Popup blocked → fall back to download
                const a = document.createElement("a");
                a.href = url;
                a.download = `Invisique_Proposal_${(clinicName || "proposal").replace(/[^\w-]+/g, "_")}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
            }
            // Release the URL after a small delay so the new tab finishes loading
            setTimeout(() => URL.revokeObjectURL(url), 30000);
            toast.success("Proposal generated");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't generate proposal.");
        } finally { setBusy(false); }
    };

    return (
        <button
            type="button"
            onClick={generate}
            disabled={busy}
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-primary text-white rounded-full px-2.5 py-1 hover:opacity-90 disabled:opacity-50 transition-opacity"
            data-testid="qa-generate-proposal"
        >
            <FilePdf size={11} weight="duotone" /> {busy ? "Generating…" : "Generate proposal"}
        </button>
    );
}

